import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { API_BASE_URL } from '../config/config';
import formatGender from '../utils/gender';

// 옛 "사용자 그룹 등록/삭제"(UserGroupRegister.js) 의 구성원 기능을 옮긴 것이다. 규격: spec D2.
// API 넷과 요청 모양(경로·파라미터·본문·헤더)은 옛 화면 그대로다 — axios 를 직접
// 부른다(useApi 아님). 모달 기본 정보에 있던 "관리자 그룹" 선택은 UserGroups.js 의
// 그룹 정보 탭이 이미 갖고 있어(admin_group_id 저장까지 포함) 여기로 옮기지 않았다.
//
// 검색 결과에서 사용자를 고르면(handleUserSelect) 그 자리에서 목록에서 지운다 — 새로
// 검색해도(handleSearch) 이미 고른 사용자는 결과에서 미리 빠진다. 그래서 "이미 고른
// 줄"이 검색 결과에 남아 있는 경우가 없다(옛 코드 그대로). 그 경우를 표시하는 비활성
// 버튼은 만들지 않았다 — 절대 켜지지 않는 코드라 만들면 죽은 코드가 된다.
const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
};

const REGISTERED_PER_PAGE = 10;
const PAGE_NUMBERS_PER_GROUP = 10;

export default function UserGroupMembers({ groupId }) {
    const [innerTab, setInnerTab] = useState('register');

    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [searchError, setSearchError] = useState(null);
    const [registering, setRegistering] = useState(false);

    const [registeredUsers, setRegisteredUsers] = useState([]);
    const [registeredUsersPage, setRegisteredUsersPage] = useState(1);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    // UserGroupRegister.js:51-119 (fetchRegisteredUsers) 그대로. 옛 화면은 "등록된 사용자
    // 그룹" 탭을 눌러야 불렀지만, 이 탭 이름 자체에 인원 수(N)를 보여줘야 해서 열자마자
    // 부른다 — groupId 가 바뀌면(다른 그룹 모달) 다시 부른다.
    const fetchRegisteredUsers = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(
                `${API_BASE_URL}/api/app-user-groups/${groupId}/users`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                        'Accept': 'application/json'
                    },
                    timeout: 15000,
                    validateStatus: (status) => status >= 200 && status < 300
                }
            );

            if (response.data && response.data.success) {
                if (response.data.data && response.data.data.length === 0) {
                    // 빈 목록은 오류가 아니다 — "등록된 사용자가 없습니다." 는 아래 렌더의
                    // sds-empty 로만 보여준다. 여기서도 error 를 세우면 같은 문구가 빨간
                    // sds-notice--error 배너로 한 번 더 찍힌다(새 그룹은 매번 이 상태다).
                    setRegisteredUsers([]);
                    setRegisteredUsersPage(1);
                    setError(null);
                } else {
                    setRegisteredUsers(response.data.data);
                    setRegisteredUsersPage(1);
                    setError(null);
                }
            } else {
                throw new Error(response.data?.message || '등록된 사용자 목록을 불러오는데 실패했습니다.');
            }
        } catch (err) {
            console.error('등록된 사용자 목록 조회 실패:', err);
            setRegisteredUsers([]);
            if (err.response) {
                if (err.response.status === 401) {
                    setError('인증이 만료되었습니다. 다시 로그인해주세요.');
                    localStorage.removeItem('token');
                } else if (err.response.status === 504) {
                    setError('서버 응답 시간이 초과되었습니다. 다시 시도해주세요.');
                } else {
                    setError(err.response.data?.message || '서버에서 오류가 발생했습니다.');
                }
            } else if (err.code === 'ECONNABORTED') {
                setError('서버 응답 시간이 초과되었습니다. 잠시 후 다시 시도해주세요.');
            } else {
                setError(err.message || '서버에 연결할 수 없습니다. 네트워크 연결을 확인해주세요.');
            }
        } finally {
            setIsLoading(false);
        }
    }, [groupId]);

    useEffect(() => { fetchRegisteredUsers(); }, [fetchRegisteredUsers]);

    // UserGroupRegister.js:177-231 (handleSearch) 그대로. 응답의 사용자 배열은
    // response.data.users 다(등록된 목록 조회의 response.data.data 와 이름이 다르다 —
    // 다른 API 라 필드명이 다르다).
    const handleSearch = async () => {
        if (!searchTerm.trim()) {
            setSearchError('검색어를 입력해주세요.');
            return;
        }

        setIsSearching(true);
        setSearchError(null);

        try {
            const token = localStorage.getItem('token');
            if (!token) {
                setSearchError('로그인이 필요합니다.');
                return;
            }

            const response = await axios.get(`${API_BASE_URL}/api/app-users/search`, {
                params: { searchTerm },
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                timeout: 30000
            });

            if (response.data && response.data.success) {
                if (response.data.users && response.data.users.length === 0) {
                    // "검색 결과가 없습니다." 는 검색 결과 칸에서만 보여준다(아래 렌더) —
                    // 옛 화면처럼 검색 오류 배너로도 같이 띄우면 같은 문구가 두 번 찍힌다.
                    setSearchResults([]);
                } else {
                    const filteredUsers = response.data.users.filter(
                        user => !selectedUsers.some(selected => selected.app_user_id === user.app_user_id)
                    );
                    setSearchResults(filteredUsers);
                }
            } else {
                throw new Error(response.data?.message || '검색 결과를 가져오는데 실패했습니다.');
            }
        } catch (err) {
            console.error('사용자 검색 오류:', err);
            if (err.response) {
                if (err.response.status === 401) {
                    setSearchError('인증이 만료되었습니다. 다시 로그인해주세요.');
                    localStorage.removeItem('token');
                } else {
                    setSearchError(err.response.data?.message || '서버에서 오류가 발생했습니다.');
                }
            } else if (err.code === 'ECONNABORTED') {
                setSearchError('서버 응답 시간이 초과되었습니다. 다시 시도해주세요.');
            } else {
                setSearchError('서버에 연결할 수 없습니다. 네트워크 연결을 확인해주세요.');
            }
        } finally {
            setIsSearching(false);
        }
    };

    // UserGroupRegister.js:233-240 그대로.
    const handleUserSelect = (user) => {
        setSelectedUsers(prev => [...prev, user]);
        setSearchResults(prev => prev.filter(u => u.app_user_id !== user.app_user_id));
    };

    const handleUserRemove = (user) => {
        setSelectedUsers(prev => prev.filter(u => u.app_user_id !== user.app_user_id));
    };

    // UserGroupRegister.js:242-288 (handleRegisterSelected) 그대로 — 끝의 목록 새로고침만
    // 페이지 전체를 새로고침하는 대신(옛 화면은 이 뒤에 alert 만 하고 부모가 다시
    // 그리지 않았다) fetchRegisteredUsers() 를 불러 "등록된 사용자 그룹" 탭 수를 맞춘다.
    const handleRegisterSelected = async () => {
        if (registering || selectedUsers.length === 0) return;

        setRegistering(true);
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                setSearchError('로그인이 필요합니다.');
                return;
            }

            const response = await axios.post(`${API_BASE_URL}/api/app-user-groups/${groupId}/users/bulk`, {
                userIds: selectedUsers.map(user => user.app_user_id)
            }, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                timeout: 30000
            });

            if (response.data.success) {
                alert('선택한 사용자들이 성공적으로 등록되었습니다.');
                setSelectedUsers([]);
                setSearchTerm('');
                setSearchResults([]);
                fetchRegisteredUsers();
            } else {
                alert('사용자 등록에 실패했습니다.');
            }
        } catch (err) {
            console.error('사용자 등록 오류:', err);
            if (err.response) {
                if (err.response.status === 401) {
                    setSearchError('인증이 만료되었습니다. 다시 로그인해주세요.');
                    localStorage.removeItem('token');
                } else {
                    setSearchError(err.response.data?.message || '서버에서 오류가 발생했습니다.');
                }
            } else if (err.code === 'ECONNABORTED') {
                setSearchError('서버 응답 시간이 초과되었습니다. 다시 시도해주세요.');
            } else {
                setSearchError('서버에 연결할 수 없습니다. 네트워크 연결을 확인해주세요.');
            }
        } finally {
            setRegistering(false);
        }
    };

    // UserGroupRegister.js:488-505 그대로 — 새로고침은 같은 요청을 부르는
    // fetchRegisteredUsers() 로 합쳤다(요청 자체는 바뀌지 않았다).
    const handleRemoveRegistered = async (user) => {
        if (!window.confirm('정말로 이 사용자를 그룹에서 삭제하시겠습니까?')) return;

        try {
            const token = localStorage.getItem('token');
            await axios.delete(`${API_BASE_URL}/api/group-app-user-member/${groupId}/${user.app_user_id}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            alert('사용자가 성공적으로 삭제되었습니다.');
            fetchRegisteredUsers();
        } catch (err) {
            alert('사용자 삭제에 실패했습니다.');
        }
    };

    const registeredTotalPages = Math.max(1, Math.ceil(registeredUsers.length / REGISTERED_PER_PAGE));
    // 쪽 번호는 10개씩 묶어 보여 준다. 200명이 넘는 그룹은 번호가 21개라 한 줄에 다 늘어놓으면
    // 넓은 모달 폭도 넘친다. 화살표로 넘어가면 다음 묶음이 나온다(메시지 관리 화면과 같은 방식).
    const pageGroupStart = Math.floor((registeredUsersPage - 1) / PAGE_NUMBERS_PER_GROUP) * PAGE_NUMBERS_PER_GROUP + 1;
    const pageGroupEnd = Math.min(pageGroupStart + PAGE_NUMBERS_PER_GROUP - 1, registeredTotalPages);
    const visiblePageNumbers = Array.from(
        { length: pageGroupEnd - pageGroupStart + 1 },
        (_, i) => pageGroupStart + i
    );
    const pagedRegisteredUsers = registeredUsers.slice(
        (registeredUsersPage - 1) * REGISTERED_PER_PAGE,
        registeredUsersPage * REGISTERED_PER_PAGE
    );

    return (
        <div className="group-members">
            <div className="sds-tabs user-groups-page__tabs" role="tablist">
                <button
                    role="tab"
                    aria-selected={innerTab === 'register'}
                    className={`sds-tabs__item${innerTab === 'register' ? ' is-active' : ''}`}
                    onClick={() => setInnerTab('register')}
                >
                    사용자 그룹 등록
                </button>
                <button
                    role="tab"
                    aria-selected={innerTab === 'registered'}
                    className={`sds-tabs__item${innerTab === 'registered' ? ' is-active' : ''}`}
                    onClick={() => setInnerTab('registered')}
                >
                    등록된 사용자 그룹 <b>{registeredUsers.length}</b>
                </button>
            </div>

            {innerTab === 'register' ? (
                <>
                    <div className="sds-toolbar">
                        <input
                            className="sds-input group-members__search"
                            placeholder="사용자 이름 또는 전화번호로 검색..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
                        />
                        <button className="sds-btn sds-btn--secondary" onClick={handleSearch} disabled={isSearching}>
                            <Search size={16} aria-hidden="true" />{isSearching ? '검색 중...' : '검색'}
                        </button>
                    </div>

                    {searchError && <div className="sds-notice sds-notice--error">{searchError}</div>}

                    <div className="group-members__split">
                        <div>
                            <h3 className="group-members__title">검색 결과</h3>
                            {searchResults.length > 0 ? (
                                <div className="sds-table-wrap">
                                    <table className="sds-table">
                                        <thead>
                                            <tr><th>이름</th><th>생년월일</th><th>전화번호</th><th>성별</th><th aria-label="작업" /></tr>
                                        </thead>
                                        <tbody>
                                            {searchResults.map((user) => (
                                                <tr key={user.app_user_id}>
                                                    <td><b>{user.name}</b></td>
                                                    <td>{user.birthdate}</td>
                                                    <td>{user.mobile}</td>
                                                    <td>{formatGender(user.gender)}</td>
                                                    <td>
                                                        <button className="sds-btn sds-btn--weak sds-btn--sm" onClick={() => handleUserSelect(user)}>
                                                            선택
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : searchTerm && !isSearching ? (
                                <div className="sds-empty">검색 결과가 없습니다.</div>
                            ) : (
                                <div className="sds-empty">이름 또는 전화번호로 검색해 주세요.</div>
                            )}
                        </div>

                        <div>
                            <h3 className="group-members__title">선택된 사용자 ({selectedUsers.length}명)</h3>
                            {selectedUsers.length > 0 ? (
                                <div className="sds-table-wrap">
                                    <table className="sds-table">
                                        <thead>
                                            <tr><th>이름</th><th>생년월일</th><th>전화번호</th><th aria-label="작업" /></tr>
                                        </thead>
                                        <tbody>
                                            {selectedUsers.map((user) => (
                                                <tr key={user.app_user_id}>
                                                    <td><b>{user.name}</b></td>
                                                    <td>{user.birthdate}</td>
                                                    <td>{user.mobile}</td>
                                                    <td>
                                                        <button className="sds-btn sds-btn--ghost sds-btn--sm" onClick={() => handleUserRemove(user)}>
                                                            취소
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div className="sds-empty">선택된 사용자가 없습니다.</div>
                            )}
                        </div>
                    </div>

                    <div className="group-members__actions">
                        <button
                            className="sds-btn sds-btn--primary"
                            onClick={handleRegisterSelected}
                            disabled={registering || selectedUsers.length === 0}
                        >
                            {registering ? '등록 중...' : '선택한 사용자 등록'}
                        </button>
                    </div>
                </>
            ) : (
                <>
                    {error && <div className="sds-notice sds-notice--error">{error}</div>}
                    {isLoading ? (
                        <div className="sds-notice">데이터를 불러오는 중입니다...</div>
                    ) : registeredUsers.length > 0 ? (
                        <>
                            <div className="sds-table-wrap">
                                <table className="sds-table">
                                    <thead>
                                        <tr><th>이름</th><th>생년월일</th><th>전화번호</th><th>성별</th><th>등록일</th><th aria-label="작업" /></tr>
                                    </thead>
                                    <tbody>
                                        {pagedRegisteredUsers.map((user) => (
                                            <tr key={user.app_user_id}>
                                                <td><b>{user.name}</b></td>
                                                <td>{user.birthdate}</td>
                                                <td>{user.mobile}</td>
                                                <td>{formatGender(user.gender)}</td>
                                                <td className="sds-table__muted">{formatDate(user.registered_at)}</td>
                                                <td>
                                                    <button className="sds-btn sds-btn--ghost sds-btn--sm" onClick={() => handleRemoveRegistered(user)}>
                                                        삭제
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <div className="sds-pagination">
                                <button
                                    className="sds-pagination__btn"
                                    onClick={() => setRegisteredUsersPage(p => Math.max(1, p - 1))}
                                    disabled={registeredUsersPage === 1}
                                    aria-label="이전 페이지"
                                >
                                    <ChevronLeft size={16} aria-hidden="true" />
                                </button>
                                {visiblePageNumbers.map((page) => (
                                    <button
                                        key={page}
                                        className={`sds-pagination__btn${registeredUsersPage === page ? ' is-active' : ''}`}
                                        aria-current={registeredUsersPage === page ? 'page' : undefined}
                                        onClick={() => setRegisteredUsersPage(page)}
                                    >
                                        {page}
                                    </button>
                                ))}
                                <button
                                    className="sds-pagination__btn"
                                    onClick={() => setRegisteredUsersPage(p => Math.min(p + 1, registeredTotalPages))}
                                    disabled={registeredUsersPage === registeredTotalPages}
                                    aria-label="다음 페이지"
                                >
                                    <ChevronRight size={16} aria-hidden="true" />
                                </button>
                            </div>
                        </>
                    ) : (
                        <div className="sds-empty">등록된 사용자가 없습니다.</div>
                    )}
                </>
            )}
        </div>
    );
}
